/**
 * A fábrica da Gráfica Boa União, como as 21 OS de amostra a mostram.
 *
 * Tudo aqui é **valor de partida**, não verdade sobre a gráfica: os setores, as
 * estações e o de-para saem do que apareceu nas OS de 31/08 e 01/09/2026. O
 * Jeferson ajusta na tela de cadastros no primeiro dia — e as perguntas que
 * faltam estão no PRD §17.1 (quantas GTO existem, quem trabalha em quê).
 *
 * O que já está confirmado por ele e é regra aqui:
 *   · GTO e Multilith são máquinas, não centro de custo.
 *   · "Click Konica/Epson" são as impressoras digitais lançadas por clique:
 *     custo, não tarefa. Ficam fora da fila do tablet.
 */

import {
  calendarioDePartida,
  MOTIVOS_DE_PARTIDA,
  PARAMETROS_DE_PARTIDA,
  type Calendario,
  type DeParaEstacao,
  type Estacao,
  type MotivoPausa,
  type Parametros,
  type Pessoa,
  type Setor,
  type TipoLinha,
} from '@pcp/dominio';

export interface Semente {
  setores: Setor[];
  estacoes: Estacao[];
  dePara: DeParaEstacao[];
  motivos: MotivoPausa[];
  pessoas: Pessoa[];
  calendario: Calendario;
  parametros: Parametros;
}

/* Identificadores fixos: a semente é reaplicável sem duplicar nada.
 *
 * ⚠️ OS IDS SÃO UUID, E NÃO OS APELIDOS — e a troca custou um defeito para ser
 * descoberta. Até 07/09/2026 eles eram legíveis (`'setor-acabamento'`), o que
 * era bom de ler e errado de sincronizar: o banco do servidor tem colunas
 * `uuid`, então a semente de lá derivava um UUID v5 do apelido. Resultado: a
 * MESMA estação tinha duas identidades, e a primeira sincronização de verdade
 * trouxe 10 setores e 36 estações — o dobro de tudo, sem erro nenhum na tela.
 *
 * Agora os dois lados usam o mesmo valor, derivado uma vez e congelado aqui. O
 * apelido fica no comentário ao lado, que é onde ele serve: para uma pessoa
 * ler. O cálculo está em `tools/semear-servidor.ts` (`uuidDe`), caso apareça
 * uma estação nova.
 *
 * ⚠️ Banco local semeado ANTES desta mudança fica com as duas cópias. Como
 * nada da Gráfica Boa União rodou ainda, o caminho é limpar o banco do
 * navegador (Cadastros › Aparelhos) e deixar semear de novo. */
/**
 * Os setores da semente.
 *
 * `SETORES.expedicao` é exportado porque o tablet precisa reconhecê-lo: a
 * expedição é o único setor que não aponta etapa — ela despacha OS pronta. É a
 * exceção de comportamento, e por isso o identificador não pode ficar escrito
 * à mão em dois lugares.
 */
export const SETORES = {
  pre: '1238130b-942e-5e46-baf6-642a086cd063' /* setor-pre-impressao */,
  offset: '282bf91c-3ec2-5eee-8ecc-3121d530a249' /* setor-impressao */,
  digital: '18554a84-8694-59ae-9865-ff030b15fd5e' /* setor-digital */,
  acabamento: '70758f65-733c-535d-888b-b7ea8a2959f8' /* setor-acabamento */,
  expedicao: '48d33c22-ee9e-55b5-b57c-d57534e429b4' /* setor-expedicao */,
} as const;

const S = SETORES;

const E = {
  ctp: '35ce1f92-c819-5605-8601-49c8956668cb' /* est-ctp */,
  sm74: '3176402c-a1f4-5336-b52a-980025038143' /* est-sm74 */,
  gto: '83e8e16a-2a47-5c53-a3e4-fb847a759afb' /* est-gto */,
  gto52: '3312753b-32b1-5518-9be6-7490b9b713e0' /* est-gto52 */,
  multilith: '4fbf0c8b-0cfb-5b28-9692-1a56839ad5a3' /* est-multilith */,
  c284: 'c3358f7b-a520-5a10-a1ea-19f38a4f6ddb' /* est-c284 */,
  epson: '6bb4ae9f-ec06-5592-a02f-5d0f8d9bd246' /* est-epson */,
  mimaki: '20406346-0946-54de-871d-49241407579e' /* est-mimaki */,
  corteDigital: 'f42b3a15-3be1-5274-8b2c-1360b117ef53' /* est-corte-digital */,
  guilhotina: '49d6c180-fb89-5108-9141-3436f4462119' /* est-guilhotina */,
  corteEspecial: '6b476838-8713-5928-95ca-b4699486814d' /* est-corte-especial */,
  envelopes: '0fb55bb7-a740-5aa6-8302-2a46e04a6ba9' /* est-envelopes */,
  dobradeira: '3fee8ad3-15e6-5645-a9af-725c4c36dbbe' /* est-dobradeira */,
  vinco: '34826735-25b9-542d-aff6-d5fbb39ba25f' /* est-vinco */,
  picotadeira: '538d5f45-8b0f-5ab1-aae5-02e4552d4527' /* est-picotadeira */,
  numerador: '65cf7f9c-8de8-5382-865e-7640a40ea9a1' /* est-numerador */,
  manual: 'dc4ebc1d-6534-5e79-8067-c581a4dc1a0d' /* est-acabamento-manual */,
  expedicao: 'e539aff8-bded-5ede-b4f9-bc5d0e0205f0' /* est-expedicao */,
} as const;

/**
 * Os ids das pessoas da semente.
 *
 * Exportado porque teste e ferramenta precisam se referir à mesma pessoa, e
 * id de uuid escrito à mão em dois lugares é id que diverge no dia em que a
 * semente muda. O apelido fica no nome do campo.
 */
export const PESSOAS = {
  daniel: '39952974-ba9f-5bd7-b8be-75e1628f3294', /* pessoa-daniel */
  encarregado: '1b49b776-bed0-5e25-b5c2-68fcdf14a634', /* pessoa-encarregado */
  expedicao: '405635d7-eb6d-5465-a1ef-f32e869036f0', /* pessoa-expedicao */
  jauci: '6c8f95b3-d37f-5987-a89d-fc810958fb4c', /* pessoa-jauci */
  jeferson: '34f5328b-78ab-5772-90b6-e47cbd834951', /* pessoa-jeferson */
  julia: 'b995b525-a224-5562-b9af-fe7e8a2bfa9f', /* pessoa-julia */
  op_acab: 'ed40f163-1e8d-5c98-a931-8b4221a9eab1', /* pessoa-op-acab */
  op_digital: 'b8674862-ea6d-5710-bf5c-f52b2b6b3888', /* pessoa-op-digital */
  op_offset: 'e799ae84-471b-57d7-8717-a71c5cc2f4fe', /* pessoa-op-offset */
  op_pre: 'c3789994-ff59-5e40-8964-ede155097e82', /* pessoa-op-pre */
} as const;

/**
 * Os ids das regras de de-para, congelados.
 *
 * ⚠️ NÃO SÃO GERADOS EM EXECUÇÃO. Eram `depara-001`, `depara-002`… montados
 * por um contador — legível, e errado pelo mesmo motivo dos setores: o
 * servidor guarda uuid, derivava um UUID v5 do apelido, e a MESMA regra ficava
 * com duas identidades. A primeira sincronização trouxe 56 regras onde havia
 * 28. Congelados aqui, os dois lados apontam para a mesma linha.
 *
 * São 40 para sobrar folga: acrescentar regra no fim da lista não renumera
 * nenhuma das anteriores, que é o que faria o de-para do servidor apontar para
 * a estação errada depois de um deploy.
 */
const IDS_DEPARA: readonly string[] = [
  '22ef8700-aa75-5e7e-8b9e-af3c1bb930cb', // depara-001
  '774b389e-a839-50fb-8720-cdb56ae7f2ea', // depara-002
  '1e3f3945-5385-51c6-b4ea-3e87bfd7e59d', // depara-003
  '86785146-3188-530b-9811-124743183cb1', // depara-004
  '56b5959b-d3e4-56f5-aad4-43d665cfadd9', // depara-005
  '15b3fa00-c3ee-524a-874b-694a4dec5933', // depara-006
  '99e908c4-0b69-5ac9-9655-dd903229bcf4', // depara-007
  'fba41ff8-8c03-5ee7-9e62-2910a6c03ab7', // depara-008
  'b2beb66c-0abc-58a1-bc14-de19dba32bd4', // depara-009
  '3dbe2538-dafa-5487-bb5b-a71d7fc3fb10', // depara-010
  '5eb2c2a7-41ea-5d98-83b1-90f83f27f0ca', // depara-011
  '9dc56fd6-f1b1-5848-947f-ae24e30bbf06', // depara-012
  'bf72650f-0621-5701-a771-8e0564cbf51f', // depara-013
  '69efc074-d11b-5f95-9388-914cbf408621', // depara-014
  '2fcce050-0d58-5dd8-8425-2a9812d63ce8', // depara-015
  '6efc1457-90a1-57a6-95f7-aae94b9685ba', // depara-016
  '3dc32e3c-d948-5b75-9b74-11e07bb810f9', // depara-017
  '5c1a94f0-518c-59a4-a494-159c03748dd9', // depara-018
  '82c482a1-5376-589c-94b0-67d514494bb4', // depara-019
  '9c6e34b4-114d-5a45-833d-6e2199f04831', // depara-020
  'ba9419f4-af26-572e-af27-e077c127f9ef', // depara-021
  '171677de-1d8f-5c98-b2a5-7df6fb722737', // depara-022
  'cdfec108-bef4-5c38-810c-d477effae9b8', // depara-023
  'e8d90f42-75c5-5ddb-a5d6-ba374cd12178', // depara-024
  'eff505d8-e267-5937-a5cd-6377993a6150', // depara-025
  '1862bc0a-020b-5ccd-b7ba-3b62c13b33a5', // depara-026
  '6f1614d5-f87f-5881-a809-05ce5d502641', // depara-027
  '4c83eb81-fcdb-5e3d-b53c-e34836c93467', // depara-028
  'f5a877c8-7e12-5afc-afb7-84d4779a24bd', // depara-029
  'a758cba3-30b7-5737-9f7f-b863169399fa', // depara-030
  '0dc6a3d4-02a5-56df-9356-8c15c79bdb08', // depara-031
  '223a0108-357f-5f99-9513-f17a76bb13f9', // depara-032
  'a0affc10-edee-5c31-aa91-636103a0f08a', // depara-033
  'e9ed47b9-0dd7-5897-b7ba-ace8b6fd4d68', // depara-034
  'a18907fc-a5fb-5ef3-8ccc-59412f706f42', // depara-035
  'cfe21a28-19a5-53d9-b289-32d3875e55a1', // depara-036
  '17db7fd4-fe89-59fd-8139-681d1e4e082e', // depara-037
  '404868ef-dc30-579a-bc0d-fb996b74e629', // depara-038
  '9c65c61a-8dff-54fd-bb3d-cf09f4fdfa35', // depara-039
  '82a276ad-773c-5f2f-b6ed-034de99333c8', // depara-040
];

/**
 * Os ids dos motivos de pausa, congelados por código.
 *
 * Mesma razão dos setores e do de-para: eram montados em execução
 * (`motivo-${codigo}`) e o servidor guardava um uuid derivado, o que dava duas
 * identidades para o mesmo motivo. O código continua sendo a chave natural dos
 * dois lados; o uuid é só a forma que o banco aceita.
 */
export const IDS_MOTIVO = {
  ACERTO: 'd951da85-f199-5aff-8d63-acd709623490',
  MANUT: '2b1e64c5-b47c-58b9-ad96-ae41a7f77d33',
  MATERIAL: 'cbc18e96-3e03-5c5e-97da-af3844470c83',
  ARTE: '20afc23e-d8e3-5046-ab55-ff021e1262de',
  ESPERA: '28fa6ad0-d126-571a-b1e5-6808aa0f8370',
  ALMOCO: '9b99ddc2-c8b7-5c22-a470-5ccad9f38db0',
  FIMTURNO: '6e9e0c98-bff0-5dcd-875f-cb1d84f27435',
  OUTRO: '47358c99-7eb8-53a2-a26a-6176caa5a6b0',
} as const;

export function semente(): Semente {
  const setores: Setor[] = [
    { id: S.pre, nome: 'Pré-impressão', ordem: 1, cor: 'var(--sys-purple)', papel: 'pre_impressao', ativo: true },
    { id: S.offset, nome: 'Impressão', ordem: 2, cor: 'var(--sys-blue)', papel: 'impressao', ativo: true },
    { id: S.digital, nome: 'Digital', ordem: 3, cor: 'var(--sys-teal)', papel: 'impressao', ativo: true },
    { id: S.acabamento, nome: 'Acabamento', ordem: 4, cor: 'var(--sys-orange)', papel: 'acabamento', ativo: true },
    { id: S.expedicao, nome: 'Expedição', ordem: 5, cor: 'var(--sys-green)', papel: 'expedicao', ativo: true },
  ];

  const est = (
    id: string,
    setorId: string,
    nome: string,
    codigo: string,
    capacidadeParalela = 1,
  ): Estacao => ({ id, setorId, nome, codigo, capacidadeParalela, ativa: true });

  const estacoes: Estacao[] = [
    est(E.ctp, S.pre, 'CTP / Gravação de chapa', 'CTP'),
    est(E.sm74, S.offset, 'Heidelberg SM 74', 'SM74'),
    est(E.gto, S.offset, 'Heidelberg GTO', 'GTO'),
    est(E.gto52, S.offset, 'Heidelberg GTO 52', 'GTO52'),
    est(E.multilith, S.offset, 'Multilith', 'MULT'),
    est(E.c284, S.digital, 'Konica Minolta C284', 'C284'),
    est(E.epson, S.digital, 'Epson', 'EPSON'),
    est(E.mimaki, S.digital, 'Mimaki (bobina)', 'MIMAKI'),
    est(E.corteDigital, S.digital, 'Plotter de corte', 'CORTE'),
    est(E.guilhotina, S.acabamento, 'Guilhotina', 'GUILH'),
    est(E.corteEspecial, S.acabamento, 'Corte especial (faca)', 'FACA'),
    est(E.envelopes, S.acabamento, 'Mesa de envelopes', 'ENV', 2),
    est(E.dobradeira, S.acabamento, 'Dobradeira', 'DOBRA'),
    est(E.vinco, S.acabamento, 'Vinco', 'VINCO'),
    est(E.picotadeira, S.acabamento, 'Picotadeira', 'PICOT'),
    est(E.numerador, S.acabamento, 'Multilith — numeração', 'NUM'),
    est(E.manual, S.acabamento, 'Acabamento manual', 'MANUAL', 3),
    est(E.expedicao, S.expedicao, 'Expedição', 'EXPED', 3),
  ];

  /**
   * O de-para. `equipamentoOs` é o texto da coluna "Equip./Fornecedor" da OS —
   * é ele que o Jeferson marcou em verde, e é por ele que se classifica.
   * `nomeEtapa` só entra quando o equipamento não basta (Gravação vem sem
   * equipamento nenhum).
   */
  let sequencia = 0;
  const regra = (
    equipamentoOs: string | null,
    estacaoId: string | null,
    tipoLinha: TipoLinha = 'operacao',
    nomeEtapa: string | null = null,
  ): DeParaEstacao => {
    sequencia += 1;
    return {
      id:
        IDS_DEPARA[sequencia - 1] ??
        /* Passou de 40 regras: erra alto em vez de inventar um id que o
           servidor não conhece e que duplicaria em silêncio. */
        (() => {
          throw new Error(
            `A semente tem mais de ${IDS_DEPARA.length} regras de de-para. ` +
              'Gere mais ids com o `uuidDe` de tools/semear-servidor.ts.',
          );
        })(),
      secao: null,
      equipamentoOs,
      nomeEtapa,
      estacaoId,
      tipoLinha,
      aprendidoEm: null,
      aprendidoPor: null,
    };
  };

  const dePara: DeParaEstacao[] = [
    // Pré-impressão. "Gravação" vem sem equipamento: casa pelo nome.
    regra('CTP', E.ctp),
    regra(null, E.ctp, 'operacao', 'Gravação'),
    regra(null, E.ctp, 'operacao', 'CTP'),

    // Offset.
    regra('SM74', E.sm74),
    regra('GTO', E.gto),
    regra('GTO52', E.gto52),
    regra('Multilith', E.multilith),

    // Digital e grande formato.
    regra('Minolta C284', E.c284),
    regra('Epson', E.epson),
    regra('Mimaki bobina', E.mimaki),
    regra('Corte Digital', E.corteDigital),

    // Acabamento.
    regra('Guilhotina', E.guilhotina),
    regra('Corte Especial', E.corteEspecial),
    regra('Envelopes', E.envelopes),
    regra('Dobradeira', E.dobradeira),
    regra('Vinco', E.vinco),
    regra('Picotadeira', E.picotadeira),
    regra('MULTILITH NUMERADOR', E.numerador),
    regra('intercalação', E.manual),
    regra('Colar Blocos', E.manual),
    regra('Grampeadeira', E.manual),
    regra('Acabamento Manual', E.manual),

    // Custo por clique: a impressora digital cobrada por página. Confirmado
    // pelo Jeferson em 05/09/2026 — não é tarefa de ninguém.
    regra('Click Konica Minolta C284', E.c284, 'custo'),
    regra('Click Epson A4', E.epson, 'custo'),
    regra('Click Epson Miolo', E.epson, 'custo'),
    regra(null, null, 'custo', 'Click Konica Minolta C284'),
    regra(null, null, 'custo', 'Click Epson A4'),
    regra(null, null, 'custo', 'Click Epson Miolo'),
  ];

  const motivos: MotivoPausa[] = MOTIVOS_DE_PARTIDA.map((m) => ({
    id:
      (IDS_MOTIVO as Record<string, string | undefined>)[m.codigo] ??
      (() => {
        throw new Error(
          `Motivo "${m.codigo}" sem id congelado em IDS_MOTIVO. ` +
            'Gere um com o `uuidDe` de tools/semear-servidor.ts.',
        );
      })(),
    codigo: m.codigo,
    descricao: m.descricao,
    categoria: m.categoria,
    exigeTexto: m.exigeTexto,
    ativo: true,
  }));

  /**
   * Pessoas de partida. Os vendedores saem das OS de amostra (são quem emite);
   * os operadores são um esqueleto para a gráfica trocar pelos nomes reais no
   * primeiro dia. O PIN de partida é o mesmo para todos, e a tela de cadastros
   * avisa enquanto ele não for trocado.
   */
  const pessoas: Pessoa[] = [
    pessoa('34f5328b-78ab-5772-90b6-e47cbd834951' /* pessoa-jeferson */, 'Jeferson', 'direcao', null, []),
    pessoa('39952974-ba9f-5bd7-b8be-75e1628f3294' /* pessoa-daniel */, 'Daniel', 'vendedor', null, []),
    pessoa('b995b525-a224-5562-b9af-fe7e8a2bfa9f' /* pessoa-julia */, 'Julia', 'vendedor', null, []),
    pessoa('6c8f95b3-d37f-5987-a89d-fc810958fb4c' /* pessoa-jauci */, 'Jauci', 'vendedor', null, []),
    pessoa('1b49b776-bed0-5e25-b5c2-68fcdf14a634' /* pessoa-encarregado */, 'Encarregado de produção', 'encarregado', '1234', [
      S.pre, S.offset, S.digital, S.acabamento, S.expedicao,
    ]),
    pessoa('c3789994-ff59-5e40-8964-ede155097e82' /* pessoa-op-pre */, 'Operador — Pré-impressão', 'operador', '1111', [S.pre]),
    pessoa('e799ae84-471b-57d7-8717-a71c5cc2f4fe' /* pessoa-op-offset */, 'Operador — Impressão', 'operador', '2222', [S.offset]),
    pessoa('b8674862-ea6d-5710-bf5c-f52b2b6b3888' /* pessoa-op-digital */, 'Operador — Digital', 'operador', '3333', [S.digital]),
    pessoa('ed40f163-1e8d-5c98-a931-8b4221a9eab1' /* pessoa-op-acab */, 'Operador — Acabamento', 'operador', '4444', [S.acabamento]),
    pessoa('405635d7-eb6d-5465-a1ef-f32e869036f0' /* pessoa-expedicao */, 'Expedição', 'expedicao', '5555', [S.expedicao]),
  ];

  return {
    setores,
    estacoes,
    dePara,
    motivos,
    pessoas,
    calendario: calendarioDePartida(),
    parametros: { ...PARAMETROS_DE_PARTIDA },
  };
}

function pessoa(
  id: string,
  nome: string,
  perfil: Pessoa['perfil'],
  pin: string | null,
  setores: string[],
): Pessoa {
  return {
    id,
    nome,
    perfil,
    // O hash real é calculado ao gravar (`repositorio.semear`); aqui vai o PIN
    // em claro só como semente, e a tela obriga a troca.
    pinHash: pin,
    setores,
    usuarioId: null,
    ativa: true,
  };
}

/** Os PINs de partida, para a tela de implantação poder mostrá-los uma vez. */
export const PINS_DE_PARTIDA: ReadonlyArray<{ pessoaId: string; pin: string }> = [
  { pessoaId: '1b49b776-bed0-5e25-b5c2-68fcdf14a634' /* pessoa-encarregado */, pin: '1234' },
  { pessoaId: 'c3789994-ff59-5e40-8964-ede155097e82' /* pessoa-op-pre */, pin: '1111' },
  { pessoaId: 'e799ae84-471b-57d7-8717-a71c5cc2f4fe' /* pessoa-op-offset */, pin: '2222' },
  { pessoaId: 'b8674862-ea6d-5710-bf5c-f52b2b6b3888' /* pessoa-op-digital */, pin: '3333' },
  { pessoaId: 'ed40f163-1e8d-5c98-a931-8b4221a9eab1' /* pessoa-op-acab */, pin: '4444' },
  { pessoaId: '405635d7-eb6d-5465-a1ef-f32e869036f0' /* pessoa-expedicao */, pin: '5555' },
];
